if (!customElements.get('kiive-feature-stories')) {
  customElements.define('kiive-feature-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.tabs = [...this.querySelectorAll('[data-story-tab]')];
      this.panels = [...this.querySelectorAll('[data-story-panel]')];
      this.details = [...this.querySelectorAll('[data-story-detail]')];
      this.detailMedia = [...this.querySelectorAll('[data-story-detail-media]')];
      this.videos = [...this.querySelectorAll('video')];
      this.visible = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPlayback = new WeakMap();
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), {signal});
        tab.addEventListener('keydown', event => {
          let next = index;
          const vertical = tab.closest('[role=tablist]')?.getAttribute('aria-orientation') === 'vertical';
          if (event.key === (vertical ? 'ArrowDown' : 'ArrowRight')) next = (index + 1) % this.tabs.length;
          else if (event.key === (vertical ? 'ArrowUp' : 'ArrowLeft')) next = (index + this.tabs.length - 1) % this.tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        }, {signal});
      });
      this.details.forEach(detail => detail.addEventListener('toggle', () => {
        if (detail.open) {
          this.details.forEach(other => { if (other !== detail) other.open = false; });
          this.detailMedia.forEach(media => { media.hidden = media.dataset.storyDetailMedia !== detail.dataset.storyDetail; });
          this.syncVideos();
        }
      }, {signal}));
      this.querySelectorAll('[data-story-modes]').forEach(group => {
        const buttons = [...group.querySelectorAll('[data-mode-button]')];
        const descriptions = [...group.querySelectorAll('[data-mode-description]')];
        buttons.forEach((button, index) => button.addEventListener('click', () => {
          buttons.forEach((item, i) => item.setAttribute('aria-pressed', String(i === index)));
          descriptions.forEach((item, i) => { item.hidden = i !== index; });
        }, {signal}));
      });
      this.videos.forEach(video => {
        // Native controls remain the fallback until this component initializes.
        video.controls = false;
        video.defaultMuted = true;
        video.muted = true;
        const button = video.parentElement.querySelector('[data-story-motion]');
        const updateLabel = () => {
          if (!button) return;
          button.textContent = video.paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        };
        video.addEventListener('play', updateLabel, {signal});
        video.addEventListener('pause', updateLabel, {signal});
        video.addEventListener('error', () => { video.controls = true; if (button) button.hidden = true; }, {signal});
        button?.addEventListener('click', () => {
          this.userPlayback.set(video, video.paused);
          this.syncVideo(video);
        }, {signal});
      });
      this.motion.addEventListener('change', () => this.syncVideos(), {signal});
      document.addEventListener('visibilitychange', () => this.syncVideos(), {signal});
      document.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.dataset.storyPanel === event.detail.blockId);
        if (index >= 0) this.select(index);
        const detail = this.details.find(item => item.dataset.storyDetail === event.detail.blockId);
        if (detail) detail.open = true;
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncVideos();
      }, {threshold: 0.05});
      this.observer.observe(this);
      this.select(0);
      this.setAttribute('data-ready', '');
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.syncVideos();
    }
    syncVideo(video) {
      const preference = this.userPlayback.get(video);
      const wantsPlayback = preference ?? (this.dataset.autoplay === 'true' && !this.motion.matches);
      const active = !video.closest('[hidden]');
      const shouldPlay = wantsPlayback && active && this.visible && !document.hidden;
      video.autoplay = shouldPlay;
      if (shouldPlay) {
        const play = video.play();
        play?.catch(() => {}); // A blocked autoplay leaves the explicit play control available.
      } else video.pause();
    }
    syncVideos() { this.videos?.forEach(video => this.syncVideo(video)); }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.videos?.forEach(video => video.pause());
      this.controller?.abort();
      this.controller = null;
    }
  });
}
